import { Flex, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiCheckSquare, FiFile, FiFolder } from "react-icons/fi";
import type { ViewDocumentAsOwnerDto } from "../../../autogen/bff-api";
import { DocumentsAsOwnerTable } from "../../../common/documents/DocumentsAsOwnerTable";
import { DownloadDocumentIconButton } from "../../../common/documents/DownloadDocumentIconButton/DownloadDocumentIconButton";
import { SectionCard } from "../../sourcing-events/portal/view-single/SectionCard";

// Takes the documents + signed-document id directly (not the bid detail) so both the buyer
// bid-detail view and the supplier read-only bid page can render it from their own DTO.
interface Props {
  documents: ViewDocumentAsOwnerDto[];
  signedDocumentId?: string | null;
  // Empty-state copy. Callers pass audience-appropriate text — the buyer sees third-person copy
  // about the supplier, the supplier sees second-person copy about their own offer.
  emptyDescription?: string;
  designContest?: boolean;
}

export const DocumentsTab = ({ documents, signedDocumentId, emptyDescription, designContest = false }: Props) => {
  const { t } = useTranslation();
  return (
    <Stack spacing="6">
      {signedDocumentId && (
        <SectionCard icon={FiCheckSquare} title={designContest ? t("Signed entry") : t("Signed offer")}>
          <Flex
            align="center"
            justify="space-between"
            border="1px solid"
            borderColor="smBorder"
            rounded="md"
            px="4"
            py="3"
          >
            <HStack spacing="3">
              <Icon as={FiFile} color="smPrimary" boxSize="5" />
              <Text fontSize="sm" color="smText">
                {designContest ? t("Digitally signed entry document") : t("Digitally signed offer document")}
              </Text>
            </HStack>
            <DownloadDocumentIconButton documentId={signedDocumentId} isSignedDocument size="sm" />
          </Flex>
        </SectionCard>
      )}
      <SectionCard icon={FiFolder} title={designContest ? t("Entry documents") : t("Offer documents")}>
        <DocumentsAsOwnerTable
          documents={documents}
          emptyDescription={emptyDescription ?? String(t("The supplier has not attached offer documents to this bid."))}
        />
      </SectionCard>
    </Stack>
  );
};
